import { Fragment, useMemo, useState } from "react"
import {
  useManagerAssignments,
  useManagerDashboard,
  useManagerRounds,
  useManagerCertificate,
} from "@/lib/queries"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Award, ChevronRight, Download, Loader2 } from "lucide-react"
import { ProgressBar, RoundGroup } from "@/components/manager/RoundGroup"
import { PersonRoundsSheet } from "@/components/manager/PersonRoundsSheet"
import { cn } from "@/lib/utils"
import type {
  Certificate,
  ManagerAssignmentRow,
  QuizRoundRow,
} from "@/lib/types"

const formatDateLong = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })
}

// Certificate dialog — fetches lazily once opened for a given assignment id
function CertificateDialog({
  assignmentId,
  open,
  onOpenChange,
}: {
  assignmentId: number | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: cert, isLoading, isError } = useManagerCertificate(
    open ? assignmentId : undefined
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="print:hidden">Certificate</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading certificate…</span>
          </div>
        ) : isError || !cert || assignmentId === undefined ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Certificate unavailable for this assignment.
          </p>
        ) : (
          <CertificateView cert={cert} assignmentId={assignmentId} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function CertificateView({ cert, assignmentId }: { cert: Certificate; assignmentId: number }) {
  return (
    <div className="flex flex-col items-center">
      {/* Download button */}
      <div className="mb-4 flex w-full justify-end print:hidden">
        <Button asChild size="sm" variant="outline">
          <a
            href={`/api/v1/manager/quiz_assignments/${assignmentId}/certificate_pdf`}
            target="_blank"
            rel="noopener"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </a>
        </Button>
      </div>

      {/* Certificate body */}
      <div className="w-full rounded-lg border-2 border-border bg-card p-10 text-center shadow-sm print:border print:shadow-none">
        <div className="mb-6 flex justify-center">
          <Award className="h-14 w-14 text-emerald-600" />
        </div>

        <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Certificate of Completion
        </p>

        <h1 className="mt-4 text-3xl font-bold tracking-tight">
          {cert.staff_name}
        </h1>

        <p className="mt-3 text-base text-muted-foreground">
          has successfully completed
        </p>

        <h2 className="mt-3 text-xl font-semibold">{cert.quiz_title}</h2>

        {/* The mark itself is deliberately absent: a certificate records
            that competency was demonstrated, not the margin by which. */}
        <div className="mx-auto mt-6 flex max-w-sm flex-col items-center gap-1">
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            Passed
          </span>
        </div>

        <div className="mt-8 flex flex-col items-center gap-1 text-sm text-muted-foreground">
          <p>
            Completed on{" "}
            <span className="font-medium text-foreground">
              {formatDateLong(cert.completed_on)}
            </span>
          </p>
          <p>Version: {cert.version_label}</p>
        </div>
      </div>
    </div>
  )
}

interface GroupSpec {
  key: string
  title: string
  round: QuizRoundRow | null
  rows: ManagerAssignmentRow[]
}

interface QuizGroups {
  open: GroupSpec[]
  closed: GroupSpec[]
}

const ONE_OFF_TITLE = "One-off assignments"

// Splits one assessment's rounds into open groups (newest round first, then
// open one-off rows) and closed groups (closed rounds, then closed one-off
// rows). Removed rows are left out here; the person sheet still shows them.
function buildGroups(
  rounds: QuizRoundRow[],
  rows: ManagerAssignmentRow[]
): QuizGroups {
  const shown = rows.filter((r) => r.closed_reason !== "removed")
  const roundGroup = (round: QuizRoundRow): GroupSpec => ({
    key: `round-${round.id}`,
    title: round.group_name,
    round,
    rows: shown.filter((r) => r.quiz_round_id === round.id),
  })
  const oneOff = (closed: boolean): GroupSpec[] => {
    const oneOffRows = shown.filter(
      (r) => r.quiz_round_id === null && (r.closed_at !== null) === closed
    )
    return oneOffRows.length === 0
      ? []
      : [ {
          key: closed ? "one-off-closed" : "one-off-open",
          title: ONE_OFF_TITLE,
          round: null,
          rows: oneOffRows,
        } ]
  }
  const newestFirst = [ ...rounds ].sort(
    (a, b) => b.opened_on.localeCompare(a.opened_on) || b.id - a.id
  )
  return {
    open: [ ...newestFirst.filter((r) => !r.closed).map(roundGroup), ...oneOff(false) ],
    closed: [ ...newestFirst.filter((r) => r.closed).map(roundGroup), ...oneOff(true) ],
  }
}

function filterGroups(groups: GroupSpec[], filter: string): GroupSpec[] {
  if (!filter) return groups
  const needle = filter.toLowerCase()
  return groups
    .map((g) => ({
      ...g,
      rows: g.rows.filter((r) => r.staff_name.toLowerCase().includes(needle)),
    }))
    .filter((g) => g.rows.length > 0)
}

function ExpandedAssessment({
  groups,
  filter,
  onSelectPerson,
}: {
  groups: QuizGroups
  filter: string
  onSelectPerson: (row: ManagerAssignmentRow) => void
}) {
  const [showClosed, setShowClosed] = useState(false)
  const open = filterGroups(groups.open, filter)
  const closed = filterGroups(groups.closed, filter)

  if (open.length === 0 && closed.length === 0) {
    return (
      <p className="px-6 py-3 text-sm text-muted-foreground">
        {filter ? "No matching people" : "No one assigned"}
      </p>
    )
  }

  return (
    <div>
      {open.map((g) => (
        <RoundGroup
          key={g.key}
          title={g.title}
          round={g.round}
          rows={g.rows}
          onSelectPerson={onSelectPerson}
        />
      ))}
      {closed.length > 0 && (
        <div className="border-t">
          <button
            type="button"
            aria-expanded={showClosed}
            onClick={() => setShowClosed((v) => !v)}
            className="flex w-full items-center gap-1.5 px-6 py-2 text-left text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                showClosed && "rotate-90"
              )}
            />
            Closed rounds ({closed.length})
          </button>
          {showClosed &&
            closed.map((g) => (
              <RoundGroup
                key={g.key}
                title={g.title}
                round={g.round}
                rows={g.rows}
                onSelectPerson={onSelectPerson}
              />
            ))}
        </div>
      )}
    </div>
  )
}

// Per-assessment progress, each row folding down to its rounds and the people
// in them. Carries no heading or page padding of its own: the page owns those.
export function AssessmentProgress() {
  const { data: summary, isLoading } = useManagerDashboard()
  const { data: assignments = [], isLoading: assignmentsLoading } =
    useManagerAssignments()
  const { data: rounds = [], isLoading: roundsLoading } = useManagerRounds()
  const [expandedQuizId, setExpandedQuizId] = useState<number | null>(null)
  // Filters people rows in every group of the expanded assessment; the
  // per-assessment counts are unaffected.
  const [staffFilter, setStaffFilter] = useState("")
  const [person, setPerson] = useState<{ quizId: number; userId: number } | null>(
    null
  )

  // Certificate dialog state
  const [certDialogOpen, setCertDialogOpen] = useState(false)
  const [certAssignmentId, setCertAssignmentId] = useState<number | undefined>(
    undefined
  )

  const openCertDialog = (id: number) => {
    setCertAssignmentId(id)
    setCertDialogOpen(true)
  }

  const groupsByQuiz = useMemo(() => {
    const rowsByQuiz = new Map<number, ManagerAssignmentRow[]>()
    for (const row of assignments) {
      const list = rowsByQuiz.get(row.quiz_id)
      if (list) list.push(row)
      else rowsByQuiz.set(row.quiz_id, [ row ])
    }
    const roundsByQuiz = new Map<number, QuizRoundRow[]>()
    for (const round of rounds) {
      const list = roundsByQuiz.get(round.quiz_id)
      if (list) list.push(round)
      else roundsByQuiz.set(round.quiz_id, [ round ])
    }
    const grouped = new Map<number, QuizGroups>()
    for (const quizId of new Set([ ...rowsByQuiz.keys(), ...roundsByQuiz.keys() ])) {
      grouped.set(
        quizId,
        buildGroups(roundsByQuiz.get(quizId) ?? [], rowsByQuiz.get(quizId) ?? [])
      )
    }
    return grouped
  }, [ assignments, rounds ])

  const selectPerson = (row: ManagerAssignmentRow) =>
    setPerson({ quizId: row.quiz_id, userId: row.user_id })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading...</span>
      </div>
    )
  }

  // Data before error, so a failed refetch does not throw away a summary we
  // already have.
  if (!summary) {
    return (
      <p className="text-sm text-muted-foreground">
        Could not load assessment progress.
      </p>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle className="text-base">Assessment Progress</CardTitle>
        <Input
          value={staffFilter}
          onChange={(e) => setStaffFilter(e.target.value)}
          placeholder="Filter by name"
          className="max-w-xs"
        />
      </CardHeader>
      <CardContent className="p-0">
        {summary.quizzes.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">
            No assessments assigned yet.
          </p>
        ) : (
          <div className="border-t">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assessment</TableHead>
                  <TableHead className="w-[220px]">Current rounds</TableHead>
                  <TableHead className="w-[120px]">Needs review</TableHead>
                  <TableHead className="w-[100px]">Overdue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.quizzes.map((quiz) => {
                  const expanded = expandedQuizId === quiz.quiz_id
                  return (
                    <Fragment key={quiz.quiz_id}>
                      <TableRow>
                        <TableCell className="font-medium">
                          <button
                            type="button"
                            aria-expanded={expanded}
                            onClick={() =>
                              setExpandedQuizId(expanded ? null : quiz.quiz_id)
                            }
                            className="flex items-center gap-1.5 text-left hover:underline"
                          >
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                                expanded && "rotate-90"
                              )}
                            />
                            {quiz.quiz_title}
                          </button>
                        </TableCell>
                        <TableCell>
                          <ProgressBar done={quiz.finished} total={quiz.assigned} />
                        </TableCell>
                        <TableCell
                          className={cn(
                            quiz.needs_review > 0
                              ? "font-medium text-sky-700 dark:text-sky-400"
                              : "text-muted-foreground"
                          )}
                        >
                          {quiz.needs_review}
                        </TableCell>
                        <TableCell
                          className={cn(
                            quiz.overdue > 0
                              ? "font-medium text-red-700 dark:text-red-400"
                              : "text-muted-foreground"
                          )}
                        >
                          {quiz.overdue}
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={4} className="bg-muted/30 p-0">
                            {assignmentsLoading || roundsLoading ? (
                              <p className="px-6 py-3 text-sm text-muted-foreground">
                                Loading rounds…
                              </p>
                            ) : (
                              <ExpandedAssessment
                                groups={
                                  groupsByQuiz.get(quiz.quiz_id) ?? { open: [], closed: [] }
                                }
                                filter={staffFilter}
                                onSelectPerson={selectPerson}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <PersonRoundsSheet
        quizId={person?.quizId ?? null}
        userId={person?.userId ?? null}
        onOpenChange={(open) => !open && setPerson(null)}
        onViewCertificate={openCertDialog}
      />

      <CertificateDialog
        assignmentId={certAssignmentId}
        open={certDialogOpen}
        onOpenChange={setCertDialogOpen}
      />
    </Card>
  )
}
